import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import './Admin.css';

const PERMISSION_OPTIONS = [
  { value: 'end_users', label: 'End users' },
  { value: 'customer_staff', label: 'Customer staff' },
  { value: 'tablemate_staff', label: 'Tablemate staff' },
];

export default function AdminApplications() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newAppName, setNewAppName] = useState('');
  const [newAppSource, setNewAppSource] = useState('s');
  const [newAppSortOrder, setNewAppSortOrder] = useState(0);
  const [newAppPermissions, setNewAppPermissions] = useState(['end_users']);
  const [addingApp, setAddingApp] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editSource, setEditSource] = useState('');
  const [editSortOrder, setEditSortOrder] = useState(0);
  const [editPermissions, setEditPermissions] = useState(['end_users']);

  const parsePermissions = (val) => {
    if (Array.isArray(val)) return val.filter((p) => PERMISSION_OPTIONS.some((o) => o.value === p));
    if (!val) return ['end_users'];
    return String(val).split(',').map((p) => p.trim()).filter((p) => PERMISSION_OPTIONS.some((o) => o.value === p));
  };

  const togglePermission = (arr, value, setter) => {
    setter((prev) => prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]);
  };

  const load = () => {
    setLoading(true);
    api.get('/api/applications.php')
      .then((data) => { setList(Array.isArray(data) ? data : []); setMessage({ type: '', text: '' }); })
      .catch((err) => setMessage({ type: 'error', text: err.error || 'Failed to load' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const addApplication = async (e) => {
    e.preventDefault();
    const name = newAppName.trim();
    if (!name) return;
    setAddingApp(true);
    setMessage({ type: '', text: '' });
    try {
      await api.post('/api/applications.php', {
        name,
        source: (newAppSource || 's').trim() || 's',
        sort_order: newAppSortOrder,
        permissions: Array.isArray(newAppPermissions) && newAppPermissions.length > 0 ? newAppPermissions : ['end_users'],
      });
      setNewAppName('');
      setNewAppSource('s');
      setNewAppSortOrder(0);
      setNewAppPermissions(['end_users']);
      load();
      setMessage({ type: 'success', text: 'Application added.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.error || 'Failed to add application' });
    } finally {
      setAddingApp(false);
    }
  };

  const startEdit = (app) => {
    setEditingId(app.id);
    setEditName(app.name);
    setEditSource(app.source ?? 's');
    setEditSortOrder(app.sort_order ?? 0);
    setEditPermissions(parsePermissions(app.permissions));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditSource('');
    setEditSortOrder(0);
    setEditPermissions(['end_users']);
  };

  const saveApplication = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    setMessage({ type: '', text: '' });
    try {
      await api.put(`/api/application.php?id=${editingId}`, {
        name: editName.trim(),
        source: (editSource || 's').trim() || 's',
        sort_order: editSortOrder,
        permissions: Array.isArray(editPermissions) && editPermissions.length > 0 ? editPermissions : ['end_users'],
      });
      cancelEdit();
      load();
      setMessage({ type: 'success', text: 'Application updated.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.error || 'Update failed' });
    }
  };

  const removeApplication = async (id) => {
    if (!window.confirm('Remove this application from the list?')) return;
    try {
      await api.delete(`/api/application.php?id=${id}`);
      load();
      setMessage({ type: 'success', text: 'Application removed.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.error || 'Delete failed' });
    }
  };

  return (
    <div className="admin-section">
      <h1>Manage applications</h1>
      <p className="admin-manage-hint">Applications appear in the category dropdown. Add new ones below.</p>
      {message.text && <div className={`admin-message ${message.type}`}>{message.text}</div>}
      <div className="admin-form admin-manage-apps">
        <form onSubmit={addApplication} className="admin-form-row-with-action" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={newAppName} onChange={(e) => setNewAppName(e.target.value)} placeholder="e.g. Back End Server Application" />
          <input type="number" value={newAppSortOrder} onChange={(e) => setNewAppSortOrder(parseInt(e.target.value, 10) || 0)} placeholder="Sort" style={{ width: '4rem' }} title="Sort order" />
          <input value={newAppSource} onChange={(e) => setNewAppSource(e.target.value)} placeholder="Source (f, b, s)" style={{ width: '5rem' }} title="URL param ?s=" />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }} title="Permissions">
            {PERMISSION_OPTIONS.map((o) => (
              <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', fontSize: '0.9em' }}>
                <input type="checkbox" checked={newAppPermissions.includes(o.value)} onChange={() => togglePermission(newAppPermissions, o.value, setNewAppPermissions)} />
                {o.label}
              </label>
            ))}
          </div>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={addingApp || !newAppName.trim()}>
            {addingApp ? 'Adding…' : 'Add application'}
          </button>
        </form>
      </div>
      <div className="admin-table-wrap">
        {loading ? <p style={{ padding: '1rem' }}>Loading…</p> : (
          <ul className="admin-app-list">
            {list.map((app) => (
              <li key={app.id}>
                {editingId === app.id ? (
                  <form onSubmit={saveApplication} className="admin-form-row-with-action" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Name" />
                    <input type="number" value={editSortOrder} onChange={(e) => setEditSortOrder(parseInt(e.target.value, 10) || 0)} placeholder="Sort" style={{ width: '4rem' }} />
                    <input value={editSource} onChange={(e) => setEditSource(e.target.value)} placeholder="Source" style={{ width: '4rem' }} />
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      {PERMISSION_OPTIONS.map((o) => (
                        <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', fontSize: '0.9em' }}>
                          <input type="checkbox" checked={editPermissions.includes(o.value)} onChange={() => togglePermission(editPermissions, o.value, setEditPermissions)} />
                          {o.label}
                        </label>
                      ))}
                    </div>
                    <button type="submit" className="admin-btn admin-btn-primary admin-btn-small">Save</button>
                    <button type="button" className="admin-btn admin-btn-small" onClick={cancelEdit}>Cancel</button>
                  </form>
                ) : (
                  <>
                    <span style={{ fontWeight: 500 }}>{app.name}</span>
                    <span style={{ color: '#666', marginLeft: '0.5rem' }}>#{app.sort_order ?? 0}</span>
                    <span style={{ color: '#888', marginLeft: '0.25rem' }}>({app.source ?? 's'})</span>
                    <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.9em' }}>
                      {parsePermissions(app.permissions).map((p) => PERMISSION_OPTIONS.find((o) => o.value === p)?.label ?? p).join(', ') || '—'}
                    </span>
                    <button type="button" className="admin-btn admin-btn-small" onClick={() => startEdit(app)}>Edit</button>
                    <button type="button" className="admin-btn admin-btn-danger admin-btn-small" onClick={() => removeApplication(app.id)}>Remove</button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {!loading && list.length === 0 && <p className="admin-manage-empty">No applications yet. Add one above.</p>}
      </div>
    </div>
  );
}
